/* eslint-disable @typescript-eslint/no-non-null-assertion */
/**

  ___                       _      ___       _        
 |   \ _  _ _ _  __ _ _ __ (_)__  |   \ __ _| |_ __ _ 
 | |) | || | ' \/ _` | '  \| / _| | |) / _` |  _/ _` |
 |___/ \_, |_||_\__,_|_|_|_|_\__| |___/\__,_|\__\__,_|
       |__/
       
*/
import { FormElements } from '../../components/form';
import { VisionApplication } from '../../application';
import { TimeSelect } from '../../components/timeselect';
import { SpreadingMap } from './map';
import { SpreaderDataDTO, SpreaderService } from './spreading_service';
import { Measurement } from '../../components/measurement';
import { HeatmapDataPoint } from '../../map/heatmap';
import { SpreaderSummaryCell } from './summary_cell';

type SeasonalKey = 'seasonalSolid' | 'seasonalPrewet' | 'seasonalAntiice';

class SpreaderAsset {
    assetId: string;
    data: SpreaderDataDTO[];
    markers: google.maps.marker.AdvancedMarkerElement[];

    public polylines: google.maps.Polyline[] = [];
    private activePolyline: google.maps.Polyline | null = null;
    private activeColor: string | null = null;

    private lastPoint: SpreaderDataDTO | null = null;
    private lastMode: number | null = null;
    private lastData: SpreaderDataDTO | null = null;
    private seasonalSolidTotal: number[] = [0, 0, 0, 0];

    constructor(assetId: string) {
        this.assetId = assetId;
        this.data = [];
        this.markers = [];
    }

    computeSeasonalTotal(key: SeasonalKey): (number | null)[] {
        // Filter items where the key exists and is not null
        const items = this.data.filter((d) => d[key] != null);
        if (items.length === 0) return [];

        // Determine the maximum length of the arrays
        const numValues = Math.max(...items.map((item) => item[key]!.length));
        const totals: (number | null)[] = [];

        for (let i = 0; i < numValues; i++) {
            // Collect all non-null values at index i
            const valuesAtIndex = items.map((item) => item[key]![i]).filter((v): v is number => v !== null);

            if (valuesAtIndex.length === 0) {
                totals.push(null); // no numbers at this index
            } else {
                const max = Math.max(...valuesAtIndex);
                const min = Math.min(...valuesAtIndex);
                totals.push(max - min);
            }
        }

        return totals;
    }

    findClosestPointOnTrack(clickLatLng: google.maps.LatLng) {
        let minDist = Infinity;
        let closestPoint = null;
        let closestIndex = -1;
        const trackPoints = this.data;

        for (let i = 0; i < trackPoints.length - 1; i++) {
            const p1 = new google.maps.LatLng(trackPoints[i].lat, trackPoints[i].lng);
            const p2 = new google.maps.LatLng(trackPoints[i + 1].lat, trackPoints[i + 1].lng);

            const projected = closestPointOnSegment(clickLatLng, p1, p2);

            const dist = google.maps.geometry.spherical.computeDistanceBetween(clickLatLng, projected);

            if (dist < minDist) {
                minDist = dist;
                closestPoint = projected;
                closestIndex = i;
            }
        }

        return {
            latLng: closestPoint,
            segmentIndex: closestIndex,
        };
    }

    clear() {
        for (const pl of this.polylines) pl.setMap(null);
        this.polylines = [];
        this.activePolyline = null;
        this.activeColor = null;
        this.lastPoint = null;
        this.data = [];
        this.seasonalSolidTotal = [0, 0, 0, 0];
    }

    private buildIfoContent(item: SpreaderDataDTO): string {
        const assetName = VisionApplication.getInstance().assetService.assetList.get(item.assetId)?.id.name || item.assetId;
        const formattedDate = new Date(item.timestamp).toLocaleDateString('en-us', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
        });
        const content = `
  <div style="
    font-size:14px;
    font-weight:600;
    margin-bottom:8px;
    color:#333;
  ">
    Spreader Info - ${assetName} 
  </div>

  <table style="
    border-collapse: collapse;
    font-size:13px;
    width:100%;
    min-width:220px;
  ">    
    <tr style="background:#f7f7f7;">
      <td style="padding:4px 8px;color:#666;">Timestamp</td>
      <td style="padding:4px 8px;">${formattedDate}</td>
    </tr>
    <tr style="background:#f7f7f7;">
      <td style="padding:4px 8px;color:#666;">Solid Mode</td>
      <td style="padding:4px 8px;">${item.solidMode}</td>
    </tr>    
    <tr>
      <td style="padding:4px 8px;color:#666;">Actual Rate</td>
      <td style="padding:4px 8px;">${Measurement.solidRate(item.solidActualRate, false).toFixed(1)} lb/mi</td>
    </tr>
    <tr style="background:#f7f7f7;">
      <td style="padding:4px 8px;color:#666;">Set Rate</td>
      <td style="padding:4px 8px;">${Measurement.solidRate(item.solidSetRate, false).toFixed(1)} lb/mi</td>
    </tr>
  </table>
`;
        return content;
    }

    public addData(item: SpreaderDataDTO, map: google.maps.Map, infoWindow: google.maps.InfoWindow): void {
        if (item.solidMode == 1) {
            // Used to have markers here
        }

        if (!this.lastPoint) {
            this.lastPoint = item;
            return;
        }

        const a = this.lastPoint;
        const b = item;

        if (a.lat === b.lat && a.lng === b.lng) {
            this.lastPoint = b;
            return;
        }

        // Decide which point controls segment color
        if (item.solidMode != null) {
            this.lastMode = item.solidMode;
            if (item.solidActualRate != null && item.solidActualRate == 0) {
                this.lastMode = 0;
            }
        }

        if (item.solidMode == null && this.lastData != null) {
            item.solidMode = this.lastData.solidMode;
            item.solidActualRate = this.lastData.solidActualRate;
            item.solidSetRate = this.lastData.solidSetRate;
        }

        const segColor = this.lastMode === 1 ? 'blue' : 'red';

        // Start a new polyline if none exists or color changed
        if (!this.activePolyline || this.activeColor !== segColor) {
            this.activeColor = segColor;

            this.activePolyline = new google.maps.Polyline({
                icons: [
                    {
                        icon: {
                            path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                            scale: 3,
                            strokeColor: '#07da57',
                        },
                        offset: '100%',
                        repeat: '120px',
                    },
                ],

                map: map,
                strokeColor: segColor,
                strokeOpacity: 4.0,
                strokeWeight: 8,
                path: [
                    { lat: a.lat, lng: a.lng },
                    { lat: b.lat, lng: b.lng },
                ],
            });

            this.activePolyline.addListener('click', (event: google.maps.PolyMouseEvent) => {
                console.log('Polyline clicked at: ', event.latLng);
                console.log('Asset ID: ', this.assetId);
                if (event.latLng != null) {
                    const nearest = this.findClosestPointOnTrack(event.latLng);
                    if (nearest) {
                        console.log(nearest.segmentIndex, this.data[nearest.segmentIndex]);
                        infoWindow.setContent(this.buildIfoContent(this.data[nearest.segmentIndex]));
                        infoWindow.setPosition(event.latLng);
                        infoWindow.open(map);
                        showTemporaryMarker(new google.maps.LatLng(this.data[nearest.segmentIndex].lat, this.data[nearest.segmentIndex].lng), map);
                    }
                }
            });

            this.polylines.push(this.activePolyline);
        } else {
            // Extend the current polyline with the new point
            this.activePolyline.getPath().push(new google.maps.LatLng(b.lat, b.lng));
        }

        // Update state
        this.lastPoint = b;
        this.lastData = item;

        this.data.push(item);
    }
}

export class VisionSpreadingTab {
    layout!: dhtmlXLayoutObject;

    rightLayout!: dhtmlXLayoutObject;

    assetGrid!: dhtmlXGridObject;
    paramForm!: dhtmlXForm;
    toolbar!: dhtmlXToolbarObject;
    tabbar!: dhtmlXTabBar;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    statusbar!: any;

    app: VisionApplication;
    map!: SpreadingMap;
    service: SpreaderService | undefined;

    assetData: Map<string, SpreaderAsset> = new Map<string, SpreaderAsset>();

    count = 0;

    summaryCell!: SpreaderSummaryCell;

    constructor() {
        this.app = VisionApplication.getInstance();
        this.service = undefined;
    }

    public init() {
        this.buildUI();
        this.setupEvents();
    }

    private buildUI(): void {
        const config: IdhtmlXLayoutConfig = {
            parent: document.body,
            pattern: '3J',
        };

        this.layout = this.app.layout.tabbar.cells('SPREADING').attachLayout(config);

        this.layout.cells('c').setText('Spreading Operations');
        this.assetGrid = this.layout.cells('c').attachGrid();
        this.layout.cells('a').hideHeader();

        this.assetGrid.setHeader('Show,Name,SALT,BRINE,KC12', ',', ['text-align:center;', 'text-align:center;', 'text-align:center;', 'text-align:center;', 'text-align:center;']);
        this.assetGrid.setColSorting('int,int,int,int,int');
        this.assetGrid.setColTypes('ch,ro,ro,ro,ro');
        this.assetGrid.setInitWidths('50,150,80,80,80');
        this.assetGrid.setColAlign('center,left,right,right,right');
        this.assetGrid.enableAutoWidth(true);
        this.assetGrid.init();

        this.toolbar = this.layout.cells('c').attachToolbar();
        this.toolbar.addButtonTwoState('tracks', 1, 'Tracks', '', '');
        this.toolbar.addButtonTwoState('heatmap', 3, 'Heatmap', '', '');

        this.toolbar.setItemState('tracks', true);
        this.toolbar.setItemState('heatmap', true);

        this.tabbar = this.layout.cells('b').attachTabbar();

        this.tabbar.addTab('map', 'MAP', 100, 1, true, false);
        //this.tabbar.addTab("dashboard", "Dashboard", 100, 2, false, false);

        this.rightLayout = this.tabbar.cells('map').attachLayout('2E');
        this.rightLayout.cells('a').hideHeader();

        this.map = new SpreadingMap(this);
        this.summaryCell = new SpreaderSummaryCell(this);

        const formData = JSON.parse(JSON.stringify(TimeSelect.SHORT_FORM));
        formData.push({ type: 'button', value: 'Search', name: 'search' });
        formData.push({
            type: 'block',
            list: [FormElements.button('pdf', 'Report', 'report'), { type: 'newcolumn' }],
        });
        formData.push({
            type: 'block',
            list: [FormElements.button('search', 'Search', 'search'), { type: 'newcolumn' }, FormElements.button('cancel', 'Cancel', 'cancel')],
        });

        this.paramForm = this.layout.cells('a').attachForm(formData);
        this.paramForm.getInput(TimeSelect.START).autocomplete = 'off';
        this.paramForm.getInput(TimeSelect.END).autocomplete = 'off';
        this.paramForm.hideItem('cancel');

        this.paramForm.attachEvent('onChange', (id, value) => {
            if (id == TimeSelect.SELECT) {
                const select = new TimeSelect(this.paramForm);
                select.selectRange(value as string);
            }
        });

        this.layout.cells('a').fixSize(false, true);
        this.layout.cells('a').setHeight(300);
        this.layout.cells('a').setWidth(400);

        this.statusbar = this.layout.cells('a').attachStatusBar();
        this.statusbar.setText('Loading Data');
        this.layout.cells('a').hideStatusBar();
    }

    private setupEvents(): void {
        this.paramForm.attachEvent('onButtonClick', (id) => {
            switch (id) {
                case 'report':
                    this.getReport();
                    break;
                case 'search':
                    this.paramForm.showItem('cancel');
                    this.getData();
                    break;
                case 'cancel': {
                    if (this.service?.fetcher) {
                        this.service.fetcher.cancel();
                        this.statusbar.setText('Loading Canceled');
                    }
                    break;
                }
            }
        });

        this.toolbar.attachEvent('onStateChange', (id, state) => {
            switch (id) {
                case 'tracks':
                    this.showTracks(state);
                    break;
                case 'heatmap':
                    this.showHeatMap(state);
                    break;
            }
        });

        this.assetGrid.attachEvent('onCheck', (rowId, _cellIndex, state) => {
            let trackstate = state;
            if (state == true && this.toolbar.getItemState('tracks') == true) {
                trackstate = true;
            } else {
                trackstate = false;
            }

            this.assetData.get(rowId)?.polylines.forEach((polyline) => {
                polyline.setMap(trackstate ? this.map.map.map : null);
            });

            let heatmapState = state;
            if (state == true && this.toolbar.getItemState('heatmap') == true) {
                heatmapState = true;
            } else {
                heatmapState = false;
            }
            this.map.heatmap.toggleAsset(rowId, heatmapState);
        });
    }

    private showHeatMap(visible: boolean): void {
        this.map.heatmap.setHeatmapVisible(visible);
    }

    private showTracks(visible: boolean): void {
        this.assetData.forEach((asset) => {
            // Make sure to only show tracks if both the track toggle is on and the asset is checked in the grid
            const isChecked = this.assetGrid.cells(asset.assetId, 0).isChecked();
            let trackVisible = visible;
            if (visible == true) {
                trackVisible = visible && isChecked;
            }
            asset.polylines.forEach((polyline) => {
                polyline.setMap(trackVisible ? this.map.map.map : null);
            });
        });
    }

    private getData(): void {
        this.assetData.forEach((asset) => {
            asset.clear();
        });
        this.assetData = new Map<string, SpreaderAsset>();

        const startTime = this.paramForm.getItemValue(TimeSelect.START).getTime();
        const endTime = this.paramForm.getItemValue(TimeSelect.END).getTime();
        if (!FormElements.verifyQueryTimes(startTime, endTime)) {
            return;
        }

        if (!this.service) {
            this.service = new SpreaderService();
        }

        this.layout.cells('a').showStatusBar();
        this.service.getSpreadingData(
            startTime,
            endTime,
            this.assetGrid.getCheckedRows(0).split(','),

            (data: SpreaderDataDTO[]) => {
                this.loadPageData(data);
            },

            () => {
                console.log('Completed fetching spreading data');
                this.onComplete();
                this.map.heatmap.setVisibleAssets(this.assetGrid.getCheckedRows(0).split(','));
            },
            (page: number, totalPages: number) => {
                this.statusbar.setText('Loading Data - Page ' + page + ' of ' + totalPages);
            }
        );

        this.summaryCell.loadSummaryData(startTime, endTime);
    }

    private loadPageData(data: SpreaderDataDTO[]): void {
        data.forEach((item) => {
            if (item.solidActualRate != null) {
                //this.map.heatmap.heatData.push({ location: new google.maps.LatLng(item.lat, item.lng), weight: item.solidActualRate, asset: item.assetId });
                this.map.heatmap.allPoints.push(new HeatmapDataPoint(new google.maps.LatLng(item.lat, item.lng), item.solidActualRate, item.assetId));
            }

            if (this.assetData.get(item.assetId) == undefined) {
                this.assetData.set(item.assetId, new SpreaderAsset(item.assetId));
            }
            this.assetData.get(item.assetId)?.addData(item, this.map.map.map, this.map.infoWindow);
        });
        this.map.heatmap.indexPoints();
    }

    private onComplete(): void {
        console.log('All data loaded');
        this.map.heatmap.setHeatmapVisible(true);
        this.statusbar.setText('Data Loaded - ' + this.assetData.size + ' assets');

        this.updateSeasonalTotals();
    }

    private getReport(): void {
        this.layout.progressOn();
        const startTime = this.paramForm.getItemValue(TimeSelect.START).getTime();
        const endTime = this.paramForm.getItemValue(TimeSelect.END).getTime();
        if (!FormElements.verifyQueryTimes(startTime, endTime)) {
            return;
        }

        if (!this.service) {
            this.service = new SpreaderService();
        }
        const body = this.assetGrid.getAllRowIds(',').split(',');
        this.service.downloadPDFPost('/report/spreader/summary/date?start=' + startTime + '&end=' + endTime, 'Spreader.pdf', body, () => {
            this.layout.progressOff();
        });
    }

    private populateGrid(): void {
        this.assetGrid.clearAll(false);
        this.count = 0;

        this.app.assetService.assetList.forEach((asset) => {
            if (asset.assetType == 'SALTER_SANDER') {
                console.log(asset.id.id);
                this.assetGrid.addRow(asset.id.id, [0, asset.id.name], 0, 0, '', false);
                this.count = this.count + 1;
            }
        });
    }

    public onSelect(): void {
        if (!this.service) {
            this.service = new SpreaderService();
        }
        this.populateGrid();
    }

    /** Updates the seasonal totals for all assets in the grid. */
    updateSeasonalTotals(): void {
        this.assetData.forEach((asset) => {
            const totals = asset.computeSeasonalTotal('seasonalSolid');
            if (totals.length === 0) return;
            let totalMat = totals[0];
            if (totalMat != null) {
                totalMat = Measurement.mass(totalMat, false) / 2000; // convert to tons
                this.assetGrid.cells(asset.assetId, 2).setValue(totalMat.toFixed(1));
            }

            const totalsPrewet = asset.computeSeasonalTotal('seasonalPrewet');
            if (totalsPrewet.length === 0) return;
            let totalPrewet = totalsPrewet[0];
            if (totalPrewet != null) {
                totalPrewet = Measurement.volume(totalPrewet, false) / 2000; // convert to tons
                this.assetGrid.cells(asset.assetId, 3).setValue(totalPrewet.toFixed(1));
            }

            const totalsAntiice = asset.computeSeasonalTotal('seasonalAntiice');
            if (totalsAntiice.length === 0) return;
            let totalAntiice = totalsAntiice[0];
            if (totalAntiice != null) {
                totalAntiice = Measurement.volume(totalAntiice, false) / 2000; // convert to tons
                this.assetGrid.cells(asset.assetId, 4).setValue(totalAntiice.toFixed(1));
            }
        });
    }
}

function closestPointOnSegment(p: google.maps.LatLng, p1: google.maps.LatLng, p2: google.maps.LatLng) {
    const x = p.lng();
    const y = p.lat();

    const x1 = p1.lng();
    const y1 = p1.lat();

    const x2 = p2.lng();
    const y2 = p2.lat();

    const dx = x2 - x1;
    const dy = y2 - y1;

    if (dx === 0 && dy === 0) return p1;

    const t = ((x - x1) * dx + (y - y1) * dy) / (dx * dx + dy * dy);

    if (t < 0) return p1;
    if (t > 1) return p2;

    return new google.maps.LatLng(y1 + t * dy, x1 + t * dx);
}

function showTemporaryMarker(closestPoint: google.maps.LatLng | null, map: google.maps.Map) {
    if (closestPoint == null) return;
    const circleEl = document.createElement('div');
    circleEl.style.width = '12px';
    circleEl.style.height = '12px';
    circleEl.style.background = 'red';
    circleEl.style.borderRadius = '50%'; // makes it a circle
    circleEl.style.border = '2px solid white'; // optional outline for visibility
    circleEl.style.boxShadow = '0 0 4px rgba(0,0,0,0.5)'; // optional shadow

    const marker = new google.maps.marker.AdvancedMarkerElement({
        map: map,
        position: closestPoint,
        title: 'Closest Point',
        content: circleEl,
    });

    // Remove marker after 5 seconds
    setTimeout(() => {
        marker.map = null; // removes it from the map
    }, 5000);
}
